import { createUseStyles } from 'react-jss'
import config from 'styles/config'
import { titleFont, textSize, backgroundColor, color as textColor, borderColor, getMainColor, getThemedColor } from 'styles/helpers'
import Color from 'color'

const themedButtonStyles = {
  default: theme => ({
    backgroundColor: theme.main_color,
    ...textColor('buttonText')
  }),
  dark: theme => {
    const mainColor = Color(theme.main_color)

    return {
      backgroundColor: 'transparent',
      color: theme.main_color,
      border: `1px solid ${mainColor.alpha(.38).rgb()}`,
    }
  },
  popin: {
    default: theme => ({}),
    dark: theme => ({
      backgroundColor: '#008CC7',
      color: '#F7F7F7'
    })
  },
  popinCancel: {
    default: theme => ({}),
    dark: theme => ({
      backgroundColor: '#fff',
      color: '#008CC7'
    })
  },
  hover: {
    default: theme => ({}),
    dark: theme => {
      const mainColor = Color(theme.main_color)

      return {
        backgroundColor: mainColor.alpha(.12).rgb().toString(),
      }
    }
  },
  active: {
    default: theme => ({}),
    dark: theme => ({
      borderColor: theme.main_color,
    })
  },
  disabled: {
    default: theme => ({
      ...backgroundColor('disabled'),
      ...textColor('textPlaceholder'),
    }),
    dark: theme => ({
      ...borderColor('disabled', 'dark'),
      ...textColor('disabled', 'dark')
    })
  },
  cancelRed: {
    default: theme => ({
      color: config.colors.fail,
    }),
    dark: theme => ({})
  },
  cancel: {
    default: theme => ({
      color: theme.main_color,
    }),
    dark: theme => ({})
  },
  cancelRedHover: {
    default: theme => {
      const mainColor = Color(config.colors.fail)

      return {
        backgroundColor: mainColor.alpha(.1).rgb().toString(),

        '&.mdc-ripple-surface:hover::before': {
          display: 'none'
        },

        '&.mdc-ripple-surface::before, &.mdc-ripple-surface::after': {
          display: 'block',
          backgroundColor: config.colors.fail,
        }
      }
    },
    dark: theme => {
      const mainColor = Color(config.colors.fail)

      return {
        '&:active': {
          border: `1px solid ${mainColor.alpha(.38).rgb()}`,
        }
      }
    }
  },
  cancelHover: {
    default: theme => {
      const mainColor = Color(theme.main_color)

      return {
        backgroundColor: mainColor.alpha(.1).rgb().toString(),

        '&.mdc-ripple-surface:hover::before': {
          display: 'none'
        },

        '&.mdc-ripple-surface::before, &.mdc-ripple-surface::after': {
          display: 'block',
          backgroundColor: theme.main_color,
        }
      }
    },
    dark: theme => {
      const mainColor = Color(theme.main_color)

      return {
        '&:active': {
          border: `1px solid ${mainColor.alpha(.38).rgb()}`,
        }
      }
    }
  },
  error: {
    default: theme => ({
      color: config.colors.fail,
    }),
    dark: theme => ({})
  },
  errorHover: {
  }
}

const themedButton = (theme, themeName) => ({
  ...themedButtonStyles[themeName](theme),

  '&$cancel': {
    ...themedButtonStyles.cancel[themeName](theme),

    '&:hover': {
      ...themedButtonStyles.cancelHover[themeName](theme),
    }
  },
  '&$cancelRed': {
    ...themedButtonStyles.cancelRed[themeName](theme),

    '&:hover': {
      ...themedButtonStyles.cancelRedHover[themeName](theme),
    }
  },
  '&$popin': {
    ...themedButtonStyles.popin[themeName](theme),
    '&:hover': {
      ...themedButtonStyles.popin[themeName](theme),
    }
  },
  '&$popinCancel': {
    ...themedButtonStyles.popinCancel[themeName](theme),
    '&:hover': {
      ...themedButtonStyles.popinCancel[themeName](theme),
    }
  },
  '&:hover': {
    ...themedButtonStyles.hover[themeName](theme),
  },

  '&:active': {
    ...themedButtonStyles.active[themeName](theme),
  },

  '&:disabled': {
    ...themedButtonStyles.disabled[themeName](theme),
  }
})

const buttonStyles = createUseStyles(theme => ({
  button: {
    display: 'inline-block',
    verticalAlign: 'top',
    padding: '0 20px',
    ...titleFont(),
    border: 'none',
    ...textSize(14, 40),
    borderRadius: 4,
    whiteSpace: 'nowrap',

    '&$cancel': {
      borderColor: 'transparent',
      backgroundColor: 'transparent',
    },

    '&:disabled': {
      cursor: 'default',
    },
  },
  defaultTheme: themedButton(theme, 'default'),
  darkTheme: themedButton(theme, 'dark'),
  cancel: {}, // must be declared for the style to exist
  error: {
    backgroundColor: config.colors.fail
  },
  cancelRed: {
    color: config.colors.fail,
    borderColor: 'transparent',
    backgroundColor: 'transparent',
  },
  popin: {},
  popinCancel: {},
  icon: {
    paddingRight: 6,
  },
  invisibleIcon: {
    position: 'relative',
    visibility: 'hidden',
  },
  spinningIcon: {
    position: 'absolute',
    left: 20,
    paddingRight: 0,
    transformOrigin: 'center',
  },
  isLoading: {
    pointerEvents: 'none',
  },
  buttonSmall: {
    padding: '0 10px',
    ...textSize(13, 26),
    backgroundColor: 'transparent',
    border: `1px solid ${theme.main_color}`,
    color: theme.main_color,

    '&:hover': {
      color: theme.main_color,
      backgroundColor: getMainColor(theme).alpha(.12).rgb().toString(),
    },

    '&:active': {
      color: theme.main_color,
      backgroundColor: getMainColor(theme).alpha(.38).rgb().toString(),
    }
  }
}))

export default buttonStyles
